import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MRT_Updater,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useMemo } from 'react';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { StatusBadge } from '@/components/ui/status-badge';
import { useUnassociatedDevicesInCurrentOrganisation } from '@/hooks/use-devices';
import { SectionContainerLayout } from '@/layouts/section-container-layout';
import {
  composeTableOptions,
  getEntityRowId,
  getRowsFromSelection,
} from '@/table';
import { DeviceAssignedToOrganisation } from '@/types/device';
import {
  getDeviceDisplayName,
  getDeviceTypeLabel,
} from '@/utils/device-helpers';
import { NewUnitWizardFooter } from '../new-unit-wizard-footer';

const columns: MRT_ColumnDef<DeviceAssignedToOrganisation>[] = [
  {
    id: 'displayName',
    accessorFn: (device) => getDeviceDisplayName(device),
    header: 'Device Name',
    grow: true,
    size: 150,
  },
  {
    id: 'serialNumber',
    accessorFn: ({ meta }) => meta?.serialNumber,
    header: 'Device Serial Number (DSN)',
    size: 150,
  },
  {
    id: 'type',
    accessorFn: ({ type }) => getDeviceTypeLabel(type),
    header: 'Type',
    size: 120,
  },
  {
    id: 'status',
    accessorFn: ({ state }) => state?.connectivity?.status,
    header: 'Status',
    grow: false,
    size: 100,
    Cell: ({ row }) => {
      const connectivityStatus =
        row.original.state?.connectivity?.status ?? 'Unknown';
      return (
        <StatusBadge
          text={connectivityStatus}
          variant={connectivityStatus === 'Online' ? 'success' : 'alert'}
        />
      );
    },
  },
];

export type AssociateDevicesStepProps = {
  selectedDevices: DeviceAssignedToOrganisation[];
  onSelectedDevicesChange: (
    selectedDevices: DeviceAssignedToOrganisation[],
  ) => void;
};

export const AssociateDevicesStep: FC<AssociateDevicesStepProps> = ({
  selectedDevices,
  onSelectedDevicesChange,
}) => {
  const { data: devices, isLoading: isLoadingDevices } =
    useUnassociatedDevicesInCurrentOrganisation();

  const rowSelection = useMemo(
    () =>
      selectedDevices.reduce((state, device) => {
        state[getEntityRowId(device)] = true;
        return state;
      }, {} as MRT_RowSelectionState),
    [selectedDevices],
  );

  const handleRowSelectionChange = (
    rowSelectionUpdater: MRT_Updater<MRT_RowSelectionState>,
  ) => {
    const newRowSelection =
      typeof rowSelectionUpdater === 'function'
        ? rowSelectionUpdater(rowSelection)
        : rowSelectionUpdater;
    const nextSelectedDevices = getRowsFromSelection(
      devices,
      newRowSelection,
      getEntityRowId,
    );

    onSelectedDevicesChange(nextSelectedDevices);
  };

  const table = useMaterialReactTable({
    ...composeTableOptions<DeviceAssignedToOrganisation>({
      enableRowSelection: true,
    }),
    columns,
    enableColumnResizing: true,
    data: devices,
    getRowId: getEntityRowId,
    state: {
      isLoading: isLoadingDevices,
      rowSelection,
      ...(isLoadingDevices
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    onRowSelectionChange: handleRowSelectionChange,
  });

  return (
    <>
      <SectionContainerLayout className="m-0 border-none p-0">
        <SectionHeadingGroup
          subtitle="Select the devices you wish to associate with this unit from the
          available device list. You can skip this step and add devices later if
          needed."
          title="Associate device(s)"
        />
        <div className="pt-8">
          <MaterialReactTable table={table} />
        </div>
      </SectionContainerLayout>
      <NewUnitWizardFooter skipable />
    </>
  );
};
